import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { IoEyeSharp, IoEyeOffSharp } from "react-icons/io5";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

// components
import MainHeader from "../../components/Header/MainHeader";
import DataSafetyMessage from "../../components/Messages/DataSafetyMessage";

// methods
import useResponse from "../../hooks/useResponse";
import AfterOnboardAPI from "../../services/AfterOnboardAPI";
import { bankDetailsValidation } from "../../validations/user.validation";
import { addAccountId, setStepperDisable } from "../../redux/slices/userSlice";
import Swal from "sweetalert2";
import { CustomButton } from "../../components";
import { googleEvent_LOJ141, googleEvent_LOJ143 } from "../../lib/google-analytics";
import { ORDER_ID_LOCAL_STORAGE_KEY } from "../../Constants/constant";

export default function EnterBankDetails() {
  const [loading, setLoading] = useState(false);
  const [bankData, setBankData] = useState(null);
  const [showAccountNo, setShowAccountNo] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [branchName, setBranchName] = useState("");

  const { leadType, repeatCustomer } = useSelector((state) => state.user);
  const { panVerifyData } = useSelector((state) => state.register);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { backScreen, shouldBankFetch } = location.state || "";

  const { addBankDetailsAPI, getBankNameWithIFSC } = new AfterOnboardAPI();
  const { handleStep } = useResponse();

  useEffect(() => {
    const fetchBankDetails = async () => {
      dispatch(setStepperDisable(true));
      const { getBankDetailsAPI } = new AfterOnboardAPI();

      const res = await getBankDetailsAPI();
      if (res && res.statusCode === 200) {
        setBankData(res.data);
      }
    };

    if (leadType !== "Repeat Case" && shouldBankFetch) {
      fetchBankDetails();
    }

    // Events
    googleEvent_LOJ141();
  }, [repeatCustomer]);

  const submitBankDetails = async (values) => {
    // setOpenDialog(false);
    setLoading(true);
    const res = await addBankDetailsAPI(values);

    if (res && res.statusCode === 200) {
      dispatch(addAccountId(res.data.accountId));

      if (backScreen) {
        window.location.href = backScreen;
      } else {
        if (res.data.status === "Not Verified") {
          navigate(res.data.route);
        } else {
          handleStep(res.data.accountId);
        }
      }
    }
    setLoading(false);
  };

  const initialValues = {
    accountHolderName: bankData?.name || panVerifyData?.full_name || "",
    accountNo: bankData?.accountNumber || "",
    confirmedAccountNo: bankData?.accountNumber || "",
    ifsc: bankData?.ifsc || "",
    bankName: bankData?.bankName || "",
  };

  const verifyAccountNumber = (number) => {
    if (bankData?.accountNumber) {
      const accountDigit = bankData.accountNumber.slice(-4);
      const valueDigit = number?.slice(-4);
      return /[Xx]/.test(accountDigit) ? true : valueDigit === accountDigit;
    }
    return false;
  };

  const validateAccountNumber = (accountNumber) => {
    if (!accountNumber) return "";
    return /[Xx]/.test(accountNumber) ? "" : accountNumber;
  };

  useEffect(() => {
   localStorage.removeItem(ORDER_ID_LOCAL_STORAGE_KEY);
  }, []);

  return (
    <div className="wrapper auto-height">
      <Formik
        initialValues={initialValues}
        validationSchema={bankDetailsValidation}
        enableReinitialize={true}
        onSubmit={(values, { setSubmitting }) => {
          const verifyAccount = verifyAccountNumber(values.accountNo);

          if (bankData?.accountNumber && !verifyAccount && bankData?.finboxExists) {
            Swal.fire({
              icon: "error",
              text: `Enter account number ending with ${bankData.accountNumber.slice(-4)}`,
              timer: 3000,
            });
            setSubmitting(false);
            return;
          }

          setSubmitting(true);

          setTimeout(async () => {
            // setOpenDialog(true);

            await submitBankDetails(values);
            setSubmitting(false);

            // Events
            googleEvent_LOJ143();
          }, 500);
        }}
      >
        {({ errors, values, touched, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit} style={{ height: "100%", minHeight: "100%" }}>
            <div className="vertical-column-layout">
              <div className="vertical-column-top">
                <MainHeader title="Bank Details" backPageName={backScreen} />
                <p className="label">Enter your Bank Details</p>
                <p className="sub-label mb-3" style={{ marginTop: -5 }}>
                  Please Enter your Bank Details for Disbursal
                </p>

                <div className="form-group">
                  <label className="label-sm">Account Holder's Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Account Holder's Name"
                    name="accountHolderName"
                    value={values.accountHolderName || ""}
                    onChange={handleChange}
                    onBlur={(e) => {
                      setFieldValue("accountHolderName", e.target.value.trim());
                    }}
                    autoComplete="off"
                  />

                  {touched.accountHolderName && errors.accountHolderName ? <div className="error-msg">{errors.accountHolderName}</div> : null}
                </div>

                <div className="form-group">
                  <label className="label-sm">Account Number</label>
                  <input
                    type={`${showAccountNo ? "password" : "tel"}`}
                    className="form-control"
                    placeholder="Enter Account Number"
                    name="accountNo"
                    value={validateAccountNumber(values.accountNo) || ""}
                    onChange={(e) => {
                      // const input = e.target.value;
                      // if (/^\d*$/.test(input)) {
                      //   setFieldValue("accountNo", e.target.value);
                      // }

                      setFieldValue("accountNo", e.target.value);
                    }}
                    onBlur={(e) => {
                      setFieldValue("accountNo", e.target.value.trim());
                    }}
                    autoComplete="off"
                    maxLength={18}
                  />

                  {touched.accountNo && errors.accountNo ? <div className="error-msg">{errors.accountNo}</div> : null}

                  <div style={{ position: "absolute", top: 48, right: 10 }}>
                    {showAccountNo ? (
                      <IoEyeSharp size={20} color="#7a7a7a" onClick={() => setShowAccountNo(!showAccountNo)} />
                    ) : (
                      <IoEyeOffSharp size={20} color="#7a7a7a" onClick={() => setShowAccountNo(!showAccountNo)} />
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Re-enter Account Number"
                    name="confirmedAccountNo"
                    value={validateAccountNumber(values.confirmedAccountNo) || ""}
                    onChange={(e) => {
                      // const input = e.target.value;
                      // if (/^\d*$/.test(input)) {
                      //   setFieldValue("confirmedAccountNo", e.target.value);
                      // }
                      setFieldValue("confirmedAccountNo", e.target.value);
                    }}
                    onBlur={(e) => {
                      setFieldValue("confirmedAccountNo", e.target.value.trim());
                    }}
                    autoComplete="off"
                    maxLength={18}
                  />

                  {touched.confirmedAccountNo && errors.confirmedAccountNo ? <div className="error-msg">{errors.confirmedAccountNo}</div> : null}
                </div>

                <div className="form-group">
                  <label className="label-sm">IFSC Code</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter IFSC Code"
                    name="ifsc"
                    value={values.ifsc || ""}
                    onChange={async (e) => {
                      setFieldValue("ifsc", e.target.value);

                      if (e.target.value.length === 11) {
                        const res = await getBankNameWithIFSC(e.target.value);
                        console.log("res-----------", res);
                        if (res.statusCode === 200) {
                          setFieldValue("bankName", res?.data?.banks[0]?.BANK);
                          setBranchName(res?.data?.banks[0]?.BRANCH);
                        }
                      } else {
                        setFieldValue("bankName", "");
                        setBranchName("");
                      }
                    }}
                    onBlur={async (e) => {
                      setFieldValue("ifsc", e.target.value.trim());
                      if (e.target.value.length === 11) {
                        const res = await getBankNameWithIFSC(e.target.value);

                        if (res.statusCode === 200) {
                          setFieldValue("bankName", res?.data?.banks[0]?.BANK);
                          setBranchName(res?.data?.banks[0]?.BRANCH);
                        }
                      } else {
                        setFieldValue("bankName", "");
                        setBranchName("");
                      }
                    }}
                    autoComplete="off"
                    maxLength={11}
                    style={{ textTransform: "uppercase" }}
                  />

                  {touched.ifsc && errors.ifsc ? <div className="error-msg">{errors.ifsc}</div> : null}
                </div>

                <div className="form-group">
                  <label className="label-sm">Bank Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter your Bank Name"
                    name="bankName"
                    value={values.bankName || ""}
                    onChange={handleChange}
                    onBlur={(e) => {
                      setFieldValue("bankName", e.target.value.trim());
                    }}
                    autoComplete="off"
                  />

                  {touched.bankName && errors.bankName ? <div className="error-msg">{errors.bankName}</div> : null}

                  {branchName && <p className="my-1">{branchName}</p>}
                </div>
              </div>

              <div className="vertical-column-bottom">
                <DataSafetyMessage />
                <CustomButton text="Next" type="submit" />
              </div>
            </div>

            <Dialog
              open={openDialog}
              fullWidth
              onClose={() => setOpenDialog(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">{"Bank Details!"}</DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">Are these information correct?</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>NO</Button>
                <Button onClick={() => submitBankDetails(values)}>YES</Button>
              </DialogActions>
            </Dialog>
          </form>
        )}
      </Formik>
    </div>
  );
}
